import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useRef, useState } from 'preact/hooks';
import { Check, AlertCircle, Loader2, Lock } from 'lucide-preact';
import { slugify, isValidSlugFormat } from '@/utils/slugify';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
/**
 * Paired name/slug input used on ClientCompany and Workspace create/edit forms.
 *
 * - Auto-slugifies from `sourceName` until the user edits the slug directly.
 * - Debounces onChange propagation to the parent form (~300ms).
 * - Debounces a live availability check via `checkAvailability`.
 * - Shows format errors locally (no network hop) against the shared rule
 *   `^[a-z0-9]+(?:-[a-z0-9]+)*$`, 3-50 chars.
 * - Can be locked (read-only) when the parent entity has an active published
 *   landing page -- renders a lock icon and a "Unpublish first" message.
 */
export function SlugInput({ value, onChange, label = 'Slug', placeholder = 'my-slug', required = false, error, helperText, sourceName, checkAvailability, locked = false, lockedMessage, disabled = false, }) {
    const [localValue, setLocalValue] = useState(value);
    const [userEdited, setUserEdited] = useState(Boolean(value));
    const [availability, setAvailability] = useState('idle');
    const checkTimer = useRef(null);
    const lastEmitted = useRef(value);
    const checkAvailabilityRef = useRef(checkAvailability);
    checkAvailabilityRef.current = checkAvailability;
    const debouncedLocal = useDebouncedValue(localValue, 300);
    // Propagate debounced value to parent.
    useEffect(() => {
        if (debouncedLocal !== lastEmitted.current) {
            lastEmitted.current = debouncedLocal;
            onChange(debouncedLocal);
        }
    }, [debouncedLocal]);
    // Sync external value changes (form reset, programmatic setValue) into local state.
    useEffect(() => {
        if (value !== lastEmitted.current) {
            setLocalValue(value);
            lastEmitted.current = value;
        }
    }, [value]);
    // Auto-populate from sourceName until user edits the slug directly.
    // Bypasses debounce -- auto-populate should feel immediate.
    useEffect(() => {
        if (userEdited || locked)
            return;
        if (!sourceName)
            return;
        const next = slugify(sourceName);
        if (next !== localValue) {
            setLocalValue(next);
            lastEmitted.current = next;
            onChange(next);
        }
    }, [sourceName, userEdited, locked]);
    // Debounced availability check -- watches localValue for responsive feedback.
    useEffect(() => {
        if (checkTimer.current) {
            window.clearTimeout(checkTimer.current);
            checkTimer.current = null;
        }
        if (locked || disabled) {
            setAvailability('idle');
            return;
        }
        if (!localValue) {
            setAvailability('idle');
            return;
        }
        if (!isValidSlugFormat(localValue)) {
            setAvailability('invalid');
            return;
        }
        if (!checkAvailabilityRef.current) {
            setAvailability('idle');
            return;
        }
        setAvailability('checking');
        checkTimer.current = window.setTimeout(async () => {
            try {
                const result = await checkAvailabilityRef.current(localValue);
                if (!result) {
                    setAvailability('idle');
                    return;
                }
                setAvailability(result.available ? 'available' : 'taken');
            }
            catch {
                setAvailability('idle');
            }
        }, 350);
        return () => {
            if (checkTimer.current)
                window.clearTimeout(checkTimer.current);
        };
    }, [localValue, locked, disabled]);
    const handleInput = (e) => {
        const raw = e.target.value;
        // Normalize as the user types: lowercase, replace spaces with dashes.
        const next = raw.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        setUserEdited(true);
        setLocalValue(next);
    };
    // Flush local value to parent on blur so the form always has the latest
    // value before a submit handler runs.
    const handleBlur = () => {
        if (localValue !== value) {
            lastEmitted.current = localValue;
            onChange(localValue);
        }
    };
    const statusIcon = () => {
        if (locked)
            return _jsx(Lock, { className: "w-4 h-4 text-slate-400" });
        if (availability === 'checking')
            return _jsx(Loader2, { className: "w-4 h-4 text-slate-400 animate-spin" });
        if (availability === 'available')
            return _jsx(Check, { className: "w-4 h-4 text-green-600" });
        if (availability === 'taken' || availability === 'invalid')
            return _jsx(AlertCircle, { className: "w-4 h-4 text-red-600" });
        return null;
    };
    const statusMessage = () => {
        if (locked)
            return lockedMessage || 'Slug is locked while this entity has a published landing page.';
        if (error)
            return error;
        if (availability === 'invalid')
            return 'Slug must be 3-50 lowercase letters, numbers, or hyphens.';
        if (availability === 'taken')
            return 'That slug is already taken.';
        if (availability === 'available')
            return 'Available';
        return helperText;
    };
    const messageClass = error || availability === 'taken' || availability === 'invalid'
        ? 'text-red-600 dark:text-red-400'
        : availability === 'available'
            ? 'text-green-600 dark:text-green-400'
            : 'text-slate-500 dark:text-slate-400';
    return (_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] }), _jsxs("div", { className: "relative", children: [_jsx("input", { type: "text", value: localValue, readOnly: locked, disabled: disabled, onInput: handleInput, onBlur: handleBlur, placeholder: placeholder, className: `w-full px-3 py-2 pr-9 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${error || availability === 'taken' || availability === 'invalid'
                            ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                            : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500'} ${locked ? 'bg-slate-100 dark:bg-slate-800 cursor-not-allowed' : ''}` }), _jsx("div", { className: "absolute inset-y-0 right-2 flex items-center", children: statusIcon() })] }), statusMessage() && _jsx("p", { className: `mt-1 text-xs ${messageClass}`, children: statusMessage() })] }));
}
